const APP_ROOTS = new Set([
  "dashboard",
  "financial",
  "master-data",
  "settings",
  "export",
  "login",
  "reset-password",
]);

export function isV2Path(pathname: string): boolean {
  return /^\/v2(?:[/?#]|$)/.test(pathname);
}

/** Strip only the version boundary, preserving the destination's search and hash. */
export function originalPath(pathname: string): string {
  if (!isV2Path(pathname)) return pathname;
  const path = pathname.replace(/^\/v2(?=[/?#]|$)/, "");
  return path.startsWith("/") ? path : `/${path}`;
}

/** Keep relative navigation, API URLs and external destinations unchanged. */
export function appPath(path: string, currentPathname: string): string {
  if (
    !isV2Path(currentPathname) ||
    isV2Path(path) ||
    !path.startsWith("/") ||
    path.startsWith("//")
  ) {
    return path;
  }
  const root = path.slice(1).split(/[/?#]/)[0] ?? "";
  return path === "/" || APP_ROOTS.has(root) ? `/v2${path}` : path;
}

function safeDestination(from: unknown): string | undefined {
  let destination: string;
  if (typeof from === "string") {
    destination = from;
  } else if (
    from &&
    typeof from === "object" &&
    "pathname" in from &&
    typeof from.pathname === "string"
  ) {
    const location = from as {
      pathname: string;
      search?: unknown;
      hash?: unknown;
    };
    destination =
      location.pathname +
      (typeof location.search === "string" ? location.search : "") +
      (typeof location.hash === "string" ? location.hash : "");
  } else {
    return undefined;
  }
  if (
    !destination.startsWith("/") ||
    destination.startsWith("//") ||
    /[\\\s]/.test(destination)
  )
    return undefined;
  const unversioned = originalPath(destination);
  const root = unversioned.slice(1).split(/[/?#]/)[0] ?? "";
  if (!APP_ROOTS.has(root) || root === "login" || root === "reset-password")
    return undefined;
  return destination;
}

/** A saved destination wins over the optional default-interface preference. */
export function loginDestination(
  pathname: string,
  from: unknown,
  preference?: string | null,
): string {
  const intended = safeDestination(from);
  if (intended) return intended;
  return isV2Path(pathname) || preference === "v2"
    ? "/v2/dashboard"
    : "/dashboard";
}

/** Interface preference is optional when browser storage is unavailable. */
export function getInterfacePreference(): string | null {
  try {
    return localStorage.getItem("travallis-interface");
  } catch {
    return null;
  }
}
